import { CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL } from '@realadvisor/common-types';
import { z } from 'zod';

import type { ThemeDocumentKind } from '@modules/_shared/components/chat/chatRecordRefresh';
import { createDocumentUrlForTemplate } from '@modules/_shared/utils/document-url';
import { docs_direct_origin } from '@shared/config';
import { type Language, getLanguage } from '@shared/LocaleContext';
import { toGlobalId } from '@shared/utils/global-id';

export const MAX_NEW_PROBLEMS = 30;

const MAX_MEASURED_PROBLEMS = 300;
const MAX_PAGES = 500;
const MAX_NEW_SELECTORS = 100;
const MAX_SELECTOR_CHARS = 200;
const MAX_TEXT_CHARS = 200;
const FETCH_TIMEOUT_MS = 30_000;
const FRAME_TIMEOUT_MS = 60_000;
const EARLY_RUN_TTL_MS = 5 * 60_000;
const MEASUREMENT_CACHE_MAX_ENTRIES = 4;
const MEASUREMENT_CACHE_TTL_MS = 5 * 60_000;

const TIMEOUT_ERROR = 'The page check timed out.';
const STOPPED_ERROR = 'The page check was stopped.';
const NOT_PREVIEWED_ERROR =
  'The page check runs only on a template that create_team_template or update_document_template wrote in this chat. Pass the templateId, baselineTemplateId, documentId and language of that result.';
const WRITE_TOOLS = ['create_team_template', 'update_document_template'];

const FRAME_STYLE =
  'position:fixed;left:0;top:0;width:1000px;height:1000px;opacity:0;pointer-events:none;z-index:-1;border:0;';

const inputSchema = z.object({
  templateId: z.string().uuid(),
  baselineTemplateId: z.string().uuid(),
  documentId: z.string().uuid(),
  language: z.string().regex(/^[a-z]{2}$/),
});

const GLOBAL_ID_TYPES: Record<ThemeDocumentKind, string> = {
  cma_report: 'CMAReport',
  lot_brochure: 'Lot',
};

const documentTypeOf = (output: Record<string, unknown>): ThemeDocumentKind =>
  output.documentType === 'lot_brochure' ? 'lot_brochure' : 'cma_report';

type TemplateLayoutInput = {
  templateId: string;
  baselineTemplateId: string;
  documentId: string;
  language: Language;
  documentType?: ThemeDocumentKind;
};

export const parseTemplateLayoutInput = (
  input: unknown,
): { ok: true; input: TemplateLayoutInput } | { ok: false; error: string } => {
  const parsed = inputSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      error:
        'The check needs templateId, baselineTemplateId and documentId as ids and a 2-letter language, as returned by create_team_template or update_document_template.',
    };
  }
  const language = getLanguage(parsed.data.language);
  if (language == null) {
    return {
      ok: false,
      error: `The language "${parsed.data.language}" is not supported here. Use the language that the template tool returned.`,
    };
  }
  return { ok: true, input: { ...parsed.data, language } };
};

const layoutProblemSchema = z.object({
  kind: z.enum([
    'overflow-bottom',
    'overflow-top',
    'overflow-side',
    'clipped-text',
    'overlapping-text',
    'invisible-text',
    'covered-text',
    'broken-image',
    'image-too-wide',
    'empty-page',
    'unused-css',
    'section-grew',
    'outside-margins',
  ]),
  page: z.number().int().min(1),
  section: z.string().max(MAX_TEXT_CHARS),
  element: z.string().max(MAX_TEXT_CHARS),
  detail: z.string().max(MAX_TEXT_CHARS),
});

const layoutMeasurementSchema = z.object({
  pages: z
    .array(
      z.object({
        page: z.number().int().min(1),
        section: z.string().max(MAX_TEXT_CHARS),
      }),
    )
    .max(MAX_PAGES),
  problems: z.array(layoutProblemSchema).max(MAX_MEASURED_PROBLEMS),
});

const newSelectorsSchema = z
  .array(z.string().min(1).max(MAX_SELECTOR_CHARS))
  .max(MAX_NEW_SELECTORS);

const frameReplySchema = z.union([
  z.object({ nonce: z.string(), result: layoutMeasurementSchema }),
  z.object({ nonce: z.string(), error: z.string().max(MAX_TEXT_CHARS) }),
]);

export type LayoutProblem = z.infer<typeof layoutProblemSchema>;

export type LayoutMeasurement = z.infer<typeof layoutMeasurementSchema>;

type TemplateLayoutCheckOutput =
  | {
      ok: true;
      documentId: string;
      language: string;
      baseline: { pageCount: number; problemCount: number };
      template: {
        pageCount: number;
        sections: Array<{ id: string; pages: number }>;
      };
      pageCountChange: number;
      grownSections: Array<{
        id: string;
        baselinePages: number;
        pages: number;
      }>;
      grownSinceLastCheck?: Array<{
        id: string;
        previousPages: number;
        pages: number;
      }>;
      newProblems: Array<LayoutProblem>;
      note: string;
    }
  | { ok: false; error: string };

const layoutProblemKey = (problem: LayoutProblem): string =>
  `${problem.kind}|${problem.section}|${problem.element}`;

const sectionPagesOf = (
  pages: LayoutMeasurement['pages'],
): Array<{ id: string; pages: number }> => {
  const sections: Array<{ id: string; pages: number }> = [];
  for (const page of pages) {
    const last = sections.at(-1);
    if (last != null && last.id === page.section) {
      last.pages += 1;
    } else {
      sections.push({ id: page.section, pages: 1 });
    }
  }
  return sections;
};

const countText = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

const grownNote = (
  grown: ReadonlyArray<{ id: string; baselinePages: number; pages: number }>,
): string =>
  grown.length === 0
    ? ''
    : ` These sections take more pages than in the baseline, which is drawn with the same theme and report, so the template change caused it, not the theme: ${grown
        .map(
          section =>
            `${section.id} (${section.baselinePages} → ${section.pages})`,
        )
        .join(
          ', ',
        )}. When your change caused it, make the section fit again (smaller sizes or spacing, or take back the block you added), then run this check again.`;

const layoutNote = (
  newProblemCount: number,
  baselinePages: number,
  templatePages: number,
): string => {
  const pages = `The template has ${countText(
    templatePages,
    'page',
    'pages',
  )} and the baseline has ${countText(baselinePages, 'page', 'pages')}.`;
  const pdf =
    'The PDF can split pages a little differently, because the PDF engine has other fonts.';
  if (newProblemCount === 0) {
    return `${pages} The template has no layout problems that the baseline does not have. ${pdf}`;
  }
  const listed =
    newProblemCount > MAX_NEW_PROBLEMS
      ? ` Only the first ${MAX_NEW_PROBLEMS} are listed.`
      : '';
  return `${pages} The template has ${countText(
    newProblemCount,
    'layout problem',
    'layout problems',
  )} that the baseline does not have.${listed} Fix the ones your change caused with update_document_template, then run this check again. ${pdf}`;
};

export const compareLayouts = (
  target: { documentId: string; language: string },
  baseline: LayoutMeasurement,
  template: LayoutMeasurement,
): TemplateLayoutCheckOutput => {
  if (baseline.pages.length === 0) {
    return {
      ok: false,
      error:
        'The baseline template did not split into pages, so the pages could not be compared.',
    };
  }
  if (template.pages.length === 0) {
    return {
      ok: false,
      error:
        'The template did not split into pages. It may not render correctly: fix it with update_document_template.',
    };
  }
  const baselineKeys = new Set(baseline.problems.map(layoutProblemKey));
  const newProblems = template.problems.filter(
    problem => !baselineKeys.has(layoutProblemKey(problem)),
  );
  const sections = sectionPagesOf(template.pages);
  const baselinePages = new Map(
    sectionPagesOf(baseline.pages).map(section => [section.id, section.pages]),
  );
  const grownSections = sections.flatMap(section => {
    const before = baselinePages.get(section.id);
    return before != null && section.pages > before
      ? [{ id: section.id, baselinePages: before, pages: section.pages }]
      : [];
  });
  const problems: LayoutProblem[] = [
    ...grownSections.map(section => ({
      kind: 'section-grew' as const,
      page: template.pages.find(page => page.section === section.id)?.page ?? 1,
      section: section.id,
      element: '',
      detail: `takes ${section.pages} pages; the baseline, drawn with the same theme, takes ${section.baselinePages}`,
    })),
    ...newProblems,
  ];
  return {
    ok: true,
    documentId: target.documentId,
    language: target.language,
    baseline: {
      pageCount: baseline.pages.length,
      problemCount: baseline.problems.length,
    },
    template: {
      pageCount: template.pages.length,
      sections,
    },
    pageCountChange: template.pages.length - baseline.pages.length,
    grownSections,
    newProblems: problems.slice(0, MAX_NEW_PROBLEMS),
    note:
      layoutNote(
        problems.length,
        baseline.pages.length,
        template.pages.length,
      ) + grownNote(grownSections),
  };
};

export const injectAfterBodyOpen = (html: string, snippet: string): string => {
  const body = /<body(?=[\s/>])[^>]*>/i.exec(html);
  if (body != null) {
    const end = body.index + body[0].length;
    return `${html.slice(0, end)}${snippet}${html.slice(end)}`;
  }
  const doctype = /^\s*<!doctype[^>]*>/i.exec(html);
  const start = doctype == null ? 0 : doctype[0].length;
  return `${html.slice(0, start)}${snippet}${html.slice(start)}`;
};

const scriptJson = (value: unknown): string =>
  JSON.stringify(value).replace(/</g, '\\u003c');

export const layoutMeasurementScript = (
  nonce: string,
  selectors: ReadonlyArray<string> = [],
): string => String.raw`
(function () {
  var nonce = ${JSON.stringify(nonce)};
  var newSelectors = ${scriptJson(selectors)};
  var maxProblems = ${MAX_MEASURED_PROBLEMS};
  var checkedTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li', 'td', 'th', 'img', 'svg', 'a'];
  var skippedTags = ['script', 'style', 'noscript', 'template', 'link', 'meta', 'title'];
  var clip = function (text, max) {
    return text.length > max ? text.slice(0, max) : text;
  };
  var textOf = function (element) {
    return clip(element.textContent || '', 400).replace(/\s+/g, ' ').trim();
  };
  var nameOf = function (element) {
    var tag = clip(element.tagName.toLowerCase(), 20);
    var className = clip((element.getAttribute('class') || '').trim().split(/\s+/)[0] || '', 40);
    return tag + (className ? '.' + className : '');
  };
  var describe = function (element) {
    var text = clip(textOf(element), 60);
    return nameOf(element) + (text ? ' "' + text + '"' : '');
  };
  var hasDirectText = function (element) {
    for (var i = 0; i < element.childNodes.length; i++) {
      var node = element.childNodes[i];
      if (node.nodeType === 3 && /\S/.test(node.textContent || '')) {
        return true;
      }
    }
    return false;
  };
  var ancestorsOf = function (element, stop) {
    var list = [];
    var current = element.parentElement;
    while (current && current !== stop) {
      list.push(current);
      current = current.parentElement;
    }
    return list;
  };
  var svgOf = function (parents) {
    for (var i = 0; i < parents.length; i++) {
      if (parents[i].tagName.toLowerCase() === 'svg') {
        return parents[i];
      }
    }
    return null;
  };
  var colorOf = function (value) {
    if (value === 'transparent') {
      return [0, 0, 0, 0];
    }
    var match = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*([\d.]+)(%?))?\s*\)$/.exec(value || '');
    if (!match) {
      return null;
    }
    var alpha = match[4] === undefined ? 1 : parseFloat(match[4]) / (match[5] ? 100 : 1);
    return [parseFloat(match[1]), parseFloat(match[2]), parseFloat(match[3]), alpha];
  };
  var luminance = function (color) {
    var channel = function (value) {
      var c = value / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(color[0]) + 0.7152 * channel(color[1]) + 0.0722 * channel(color[2]);
  };
  var contrastOf = function (a, b) {
    var light = Math.max(luminance(a), luminance(b));
    var dark = Math.min(luminance(a), luminance(b));
    return (light + 0.05) / (dark + 0.05);
  };
  var backgroundOf = function (chain) {
    for (var i = 0; i < chain.length; i++) {
      var style = getComputedStyle(chain[i]);
      if (style.backgroundImage && style.backgroundImage !== 'none') {
        return null;
      }
      var color = colorOf(style.backgroundColor);
      if (color && color[3] >= 0.9) {
        return color;
      }
      if (!color || color[3] > 0) {
        return null;
      }
    }
    return [255, 255, 255, 1];
  };
  var layerOf = function (chain) {
    for (var i = 0; i < chain.length; i++) {
      var style = getComputedStyle(chain[i]);
      if (style.position && style.position !== 'static') {
        var z = parseInt(style.zIndex, 10);
        return isNaN(z) ? 0 : z;
      }
    }
    return -0.5;
  };
  var overlapShare = function (a, b) {
    var width = Math.min(a.right, b.right) - Math.max(a.left, b.left);
    var height = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
    if (width <= 0 || height <= 0) {
      return 0;
    }
    var smaller = Math.min((a.right - a.left) * (a.bottom - a.top), (b.right - b.left) * (b.bottom - b.top));
    return smaller > 0 ? (width * height) / smaller : 0;
  };
  var underReported = function (parents, reported) {
    for (var i = 0; i < parents.length; i++) {
      if (reported.indexOf(parents[i]) !== -1) {
        return true;
      }
    }
    return false;
  };
  var clippedBelow = function (parents, bottom) {
    for (var i = 0; i < parents.length; i++) {
      if (getComputedStyle(parents[i]).overflowY !== 'visible' && parents[i].getBoundingClientRect().bottom <= bottom + 1) {
        return true;
      }
    }
    return false;
  };
  var clippedAbove = function (parents, top) {
    for (var i = 0; i < parents.length; i++) {
      if (getComputedStyle(parents[i]).overflowY !== 'visible' && parents[i].getBoundingClientRect().top >= top - 1) {
        return true;
      }
    }
    return false;
  };
  var clippedAside = function (parents, pageBox) {
    for (var i = 0; i < parents.length; i++) {
      var box = parents[i].getBoundingClientRect();
      if (getComputedStyle(parents[i]).overflowX !== 'visible' && box.left >= pageBox.left - 1 && box.right <= pageBox.right + 1) {
        return true;
      }
    }
    return false;
  };
  var textRectsOf = function (element) {
    if (typeof document.createRange !== 'function') {
      return element.getClientRects();
    }
    var rects = [];
    for (var n = 0; n < element.childNodes.length; n++) {
      var node = element.childNodes[n];
      if (node.nodeType === 3 && /\S/.test(node.textContent || '')) {
        var range = document.createRange();
        range.selectNodeContents(node);
        var lineRects = range.getClientRects();
        for (var r = 0; r < lineRects.length; r++) {
          rects.push(lineRects[r]);
        }
      }
    }
    return rects;
  };
  var sidePaddingOf = function (element) {
    var style = getComputedStyle(element);
    return { left: parseFloat(style.paddingLeft) || 0, right: parseFloat(style.paddingRight) || 0, clips: style.overflowX !== 'visible' };
  };
  var paddedHolderOf = function (parents) {
    for (var i = 0; i < parents.length; i++) {
      var padding = sidePaddingOf(parents[i]);
      if (padding.left >= 16 || padding.right >= 16) {
        return { element: parents[i], padding: padding };
      }
      if (padding.clips) {
        return null;
      }
    }
    return null;
  };
  var sectionOf = function (page, elements) {
    var id = page.id || page.getAttribute('data-split-from') || '';
    for (var i = 0; !id && i < elements.length; i++) {
      var candidate = elements[i].id || elements[i].getAttribute('data-split-from') || '';
      if (candidate.indexOf('page-') === 0) {
        id = candidate;
      }
    }
    return clip(id, 80);
  };
  var measure = function () {
    var pages = [];
    var problems = [];
    var seen = Object.create(null);
    var addProblem = function (kind, page, section, element, detail) {
      var description = element ? describe(element) : '';
      var key = kind + '|' + section + '|' + description;
      if (seen[key] || problems.length >= maxProblems) {
        return;
      }
      seen[key] = true;
      problems.push({ kind: kind, page: page, section: section, element: description, detail: detail });
    };
    var pageElements = document.querySelectorAll('.pagedjs_pages > .pagedjs_page');
    for (var p = 0; p < pageElements.length; p++) {
      var page = pageElements[p];
      var pageNumber = parseInt(page.getAttribute('data-page-number') || '', 10);
      if (!(pageNumber >= 1)) {
        pageNumber = p + 1;
      }
      var content = page.querySelector('.pagedjs_page_content') || page;
      var elements = content.querySelectorAll('*');
      var section = sectionOf(page, elements);
      var pageBox = page.getBoundingClientRect();
      var contentBox = content.getBoundingClientRect();
      var reported = { bottom: [], top: [], side: [], clipped: [], wide: [], invisible: [], margins: [] };
      var pageTexts = [];
      var hasContent = false;
      var covers = [];
      var chartTexts = [];
      pages.push({ page: pageNumber, section: section });
      for (var c = 0; c < elements.length; c++) {
        var coverStyle = getComputedStyle(elements[c]);
        if ((coverStyle.position === 'absolute' || coverStyle.position === 'fixed') && coverStyle.visibility === 'visible') {
          var coverBox = elements[c].getBoundingClientRect();
          var coverColor = colorOf(coverStyle.backgroundColor);
          var opaque = elements[c].tagName.toLowerCase() === 'img' || (coverColor && coverColor[3] >= 0.5) || (coverStyle.backgroundImage && coverStyle.backgroundImage !== 'none');
          if (opaque && (coverBox.right - coverBox.left) * (coverBox.bottom - coverBox.top) >= 400) {
            var z = parseInt(coverStyle.zIndex, 10);
            covers.push({ element: elements[c], order: c, box: coverBox, layer: isNaN(z) ? 0 : z, parents: ancestorsOf(elements[c], content) });
          }
        }
      }
      for (var i = 0; i < elements.length; i++) {
        var element = elements[i];
        var tag = element.tagName.toLowerCase();
        if (skippedTags.indexOf(tag) !== -1) {
          continue;
        }
        var parents = ancestorsOf(element, content);
        var svg = svgOf(parents);
        if (svg) {
          var textBox = element.getBoundingClientRect();
          if (tag === 'text' && textOf(element) !== '' && (textBox.width > 0 || textBox.height > 0)) {
            var chartClips = getComputedStyle(svg).overflowX !== 'visible';
            var edge = chartClips ? svg.getBoundingClientRect() : pageBox;
            var outside = Math.max(edge.left - textBox.left, textBox.right - edge.right, edge.top - textBox.top, textBox.bottom - edge.bottom);
            if (outside > 2) {
              addProblem(chartClips ? 'clipped-text' : 'overflow-side', pageNumber, section, element, 'chart text goes ' + Math.round(outside) + 'px past the edge of the ' + (chartClips ? 'chart and is cut off' : 'page'));
            }
            for (var t = 0; t < chartTexts.length; t++) {
              if (chartTexts[t].svg === svg && overlapShare(textBox, chartTexts[t].box) > 0.3) {
                addProblem('overlapping-text', pageNumber, section, element, 'chart text overlaps the chart text "' + clip(textOf(chartTexts[t].element), 60) + '"');
                break;
              }
            }
            chartTexts.push({ svg: svg, box: textBox, element: element });
          }
          continue;
        }
        if (tag === 'img' && element.complete && element.naturalWidth === 0 && element.getClientRects().length > 0) {
          addProblem('broken-image', pageNumber, section, element, 'the image did not load');
        }
        var box = element.getBoundingClientRect();
        var style = getComputedStyle(element);
        if ((box.width <= 0 && box.height <= 0) || style.visibility !== 'visible') {
          continue;
        }
        var directText = hasDirectText(element);
        if (directText || tag === 'img' || tag === 'svg') {
          hasContent = true;
        }
        var ellipsis = style.textOverflow === 'ellipsis';
        if ((style.overflowY !== 'visible' || ellipsis) && tag !== 'img' && tag !== 'svg' && !underReported(parents, reported.clipped) && textOf(element) !== '') {
          var tall = element.scrollHeight > element.clientHeight + 2;
          if (tall || (ellipsis && element.scrollWidth > element.clientWidth + 2)) {
            addProblem('clipped-text', pageNumber, section, element, tall ? 'text is cut off: ' + element.scrollHeight + 'px of text in a ' + element.clientHeight + 'px box' : 'text is cut off at the side');
            reported.clipped.push(element);
          }
        }
        var fill = colorOf(style.backgroundColor);
        var paintsBox = (fill && fill[3] > 0) || (parseFloat(style.borderLeftWidth) || 0) > 0 || (parseFloat(style.borderRightWidth) || 0) > 0;
        if ((directText || tag === 'img' || paintsBox) && !underReported(parents, reported.margins)) {
          var holder = paddedHolderOf(parents);
          if (holder) {
            var holderBox = holder.element.getBoundingClientRect();
            var beyond = Math.max(holderBox.left + holder.padding.left - box.left, box.right - (holderBox.right - holder.padding.right));
            if (beyond > 2) {
              addProblem('outside-margins', pageNumber, section, element, clip('goes ' + Math.round(beyond) + 'px past the side margin of ' + nameOf(holder.element), 200));
              reported.margins.push(element);
            }
          }
        }
        if (checkedTags.indexOf(tag) === -1 && !directText) {
          continue;
        }
        if (box.bottom > contentBox.bottom + 1 && !underReported(parents, reported.bottom) && !clippedBelow(parents, contentBox.bottom)) {
          addProblem('overflow-bottom', pageNumber, section, element, 'goes ' + Math.round(box.bottom - contentBox.bottom) + 'px below the page area');
          reported.bottom.push(element);
        }
        var past = Math.max(pageBox.left - box.left, box.right - pageBox.right);
        if (past > 1 && !underReported(parents, reported.side) && !clippedAside(parents, pageBox)) {
          addProblem('overflow-side', pageNumber, section, element, 'goes ' + Math.round(past) + 'px past the page edge');
          reported.side.push(element);
        }
        if (box.top < pageBox.top - 1 && !underReported(parents, reported.top) && !clippedAbove(parents, pageBox.top)) {
          addProblem('overflow-top', pageNumber, section, element, 'goes ' + Math.round(pageBox.top - box.top) + 'px above the page edge');
          reported.top.push(element);
        }
        if (directText) {
          var chain = [element].concat(parents);
          var ink = colorOf(style.color);
          var ground = backgroundOf(chain.concat([content]));
          if (ink && ground && (ink[3] < 0.1 || contrastOf(ink, ground) < 1.25) && !underReported(parents, reported.invisible)) {
            addProblem('invisible-text', pageNumber, section, element, 'the text has almost the same colour as its background');
            reported.invisible.push(element);
          }
          var ownRects = textRectsOf(element);
          var overlapped = false;
          for (var w = 0; w < pageTexts.length && !overlapped; w++) {
            var other = pageTexts[w];
            if (parents.indexOf(other.element) !== -1 || other.parents.indexOf(element) !== -1) {
              continue;
            }
            for (var a = 0; a < ownRects.length && !overlapped; a++) {
              for (var b = 0; b < other.rects.length; b++) {
                if (overlapShare(ownRects[a], other.rects[b]) > 0.3) {
                  overlapped = true;
                  addProblem('overlapping-text', pageNumber, section, element, clip('the text overlaps the text "' + clip(textOf(other.element), 60) + '"', 200));
                  break;
                }
              }
            }
          }
          if (pageTexts.length < 400) {
            pageTexts.push({ element: element, rects: ownRects, parents: parents });
          }
          var textLayer = layerOf(chain);
          var middleX = (box.left + box.right) / 2;
          var middleY = (box.top + box.bottom) / 2;
          for (var k = 0; k < covers.length; k++) {
            var cover = covers[k];
            if (cover.element === element || parents.indexOf(cover.element) !== -1 || cover.parents.indexOf(element) !== -1) {
              continue;
            }
            var above = cover.layer > textLayer || (cover.layer === textLayer && cover.order > i);
            if (above && middleX > cover.box.left && middleX < cover.box.right && middleY > cover.box.top && middleY < cover.box.bottom) {
              if (cover.problem) {
                cover.more += 1;
                cover.problem.detail = clip('the text and ' + cover.more + ' more ' + (cover.more === 1 ? 'text is' : 'texts are') + ' under ' + describe(cover.element), 200);
              } else {
                var count = problems.length;
                addProblem('covered-text', pageNumber, section, element, clip('the text is under ' + describe(cover.element), 200));
                if (problems.length > count) {
                  cover.problem = problems[count];
                  cover.more = 0;
                }
              }
              break;
            }
          }
        }
        if (tag === 'img' && box.width > contentBox.width + 1 && !underReported(parents, reported.wide)) {
          addProblem('image-too-wide', pageNumber, section, element, 'the image is ' + Math.round(box.width) + 'px wide but the page area is ' + Math.round(contentBox.width) + 'px');
          reported.wide.push(element);
        }
      }
      if (!hasContent) {
        addProblem('empty-page', pageNumber, section, null, 'the page has no text and no image');
      }
    }
    for (var u = 0; u < newSelectors.length && problems.length < maxProblems; u++) {
      var matched = true;
      try {
        matched = document.querySelectorAll(newSelectors[u]).length > 0;
      } catch (error) {
        matched = true;
      }
      var pageIds = newSelectors[u].match(/#page-[a-z0-9-]+/g) || [];
      for (var q = 0; q < pageIds.length && !matched; q++) {
        try {
          matched = document.querySelectorAll(pageIds[q]).length === 0;
        } catch (error) {
          matched = true;
        }
      }
      if (!matched) {
        problems.push({ kind: 'unused-css', page: 1, section: '', element: clip(newSelectors[u], ${MAX_SELECTOR_CHARS}), detail: 'this new CSS rule matches no element in this report: fix the selector, unless it styles content this report does not show (a block behind a condition that is off here)' });
      }
    }
    return { pages: pages, problems: problems };
  };
  var report = function () {
    var message;
    try {
      message = { nonce: nonce, result: measure() };
    } catch (error) {
      message = { nonce: nonce, error: clip(String((error && error.message) || error), 180) };
    }
    window.parent.postMessage(message, '*');
  };
  window.addEventListener('pagedjs:rendering:complete', function () {
    setTimeout(report, 300);
  }, { once: true });
})();
`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null;

type Step<T> = { ok: true; value: T } | { ok: false; error: string };

export type LayoutCheckMessage = { parts?: ReadonlyArray<unknown> };

const isToolPartNamed = (
  part: Record<string, unknown>,
  toolName: string,
): boolean =>
  part.type === `tool-${toolName}` ||
  (part.type === 'dynamic-tool' && part.toolName === toolName);

const sameId = (value: unknown, id: string): boolean =>
  typeof value === 'string' && value.toLowerCase() === id.toLowerCase();

type WriteTarget = {
  templateId: string;
  baselineTemplateId: string;
  documentId: string;
  language: string;
};

const findWritten = (
  messages: ReadonlyArray<LayoutCheckMessage>,
  target: WriteTarget,
): { output: Record<string, unknown>; later: unknown[] } | null => {
  const parts = messages.flatMap(message => message.parts ?? []);
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const part = parts[index];
    if (
      !isRecord(part) ||
      !WRITE_TOOLS.some(tool => isToolPartNamed(part, tool)) ||
      part.state !== 'output-available' ||
      !isRecord(part.output)
    ) {
      continue;
    }
    const output = part.output;
    const template = isRecord(output.template) ? output.template : {};
    if (
      output.ok === true &&
      output.rendered === true &&
      sameId(template.id, target.templateId) &&
      sameId(output.baselineTemplateId, target.baselineTemplateId) &&
      sameId(output.documentId, target.documentId) &&
      output.language === target.language
    ) {
      return { output, later: parts.slice(index + 1) };
    }
  }
  return null;
};

const findWrittenOutput = (
  messages: ReadonlyArray<LayoutCheckMessage>,
  target: WriteTarget,
): Record<string, unknown> | null =>
  findWritten(messages, target)?.output ?? null;

const selectorsOf = (output: Record<string, unknown> | null): string[] => {
  const parsed = newSelectorsSchema.safeParse(output?.newSelectors);
  return parsed.success ? [...new Set(parsed.data)] : [];
};

export const wasWritten = (
  messages: ReadonlyArray<LayoutCheckMessage>,
  target: WriteTarget,
): boolean => findWrittenOutput(messages, target) != null;

export const writtenSelectors = (
  messages: ReadonlyArray<LayoutCheckMessage>,
  target: WriteTarget,
): string[] => selectorsOf(findWrittenOutput(messages, target));

export const unsettledLayoutCheckIds = (
  parts: ReadonlyArray<unknown>,
  started: ReadonlySet<string>,
): string[] =>
  parts.flatMap(part =>
    isRecord(part) &&
    isToolPartNamed(part, CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL) &&
    part.state === 'input-available' &&
    typeof part.toolCallId === 'string' &&
    !started.has(part.toolCallId)
      ? [part.toolCallId]
      : [],
  );

export const growthSinceLastCheck = (
  messages: ReadonlyArray<LayoutCheckMessage>,
  target: TemplateLayoutInput,
  sections: ReadonlyArray<{ id: string; pages: number }>,
): Array<{ id: string; previousPages: number; pages: number }> => {
  for (const message of [...messages].reverse()) {
    for (const part of [...(message.parts ?? [])].reverse()) {
      if (
        !isRecord(part) ||
        !isToolPartNamed(part, CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL) ||
        part.state !== 'output-available' ||
        !isRecord(part.input) ||
        !isRecord(part.output) ||
        part.output.ok !== true ||
        !sameId(part.input.templateId, target.templateId) ||
        !sameId(part.input.documentId, target.documentId) ||
        part.input.language !== target.language
      ) {
        continue;
      }
      const template = isRecord(part.output.template)
        ? part.output.template
        : {};
      const previous = new Map(
        (Array.isArray(template.sections) ? template.sections : [])
          .filter(isRecord)
          .map(section => [section.id, section.pages]),
      );
      return sections.flatMap(section => {
        const before = previous.get(section.id);
        return typeof before === 'number' && section.pages > before
          ? [{ id: section.id, previousPages: before, pages: section.pages }]
          : [];
      });
    }
  }
  return [];
};

export const layoutCheckFailed = (output: unknown): boolean =>
  !(isRecord(output) && output.ok === true);

export const stoppedLayoutCheckOutput = (): { ok: false; error: string } => ({
  ok: false,
  error: STOPPED_ERROR,
});

export const parseFrameReply = (
  data: unknown,
  nonce: string,
): Step<LayoutMeasurement> | null => {
  if (!isRecord(data) || data.nonce !== nonce) {
    return null;
  }
  const reply = frameReplySchema.safeParse(data);
  if (!reply.success) {
    return {
      ok: false,
      error: 'The page check sent back data it could not read.',
    };
  }
  if ('error' in reply.data) {
    return { ok: false, error: `The page check failed: ${reply.data.error}` };
  }
  return { ok: true, value: reply.data.result };
};

const documentHtmlUrl = (
  templateId: string,
  documentId: string,
  language: Language,
  documentType: ThemeDocumentKind,
): URL => {
  const url = createDocumentUrlForTemplate(
    templateId,
    toGlobalId(GLOBAL_ID_TYPES[documentType], documentId),
    language,
  );
  return new URL(`${url.pathname}${url.search}`, docs_direct_origin);
};

const fetchDocumentHtml = async (
  url: URL,
  label: string,
  signal: AbortSignal,
): Promise<Step<string>> => {
  const request = new AbortController();
  let timedOut = false;
  const timer = window.setTimeout(() => {
    timedOut = true;
    request.abort();
  }, FETCH_TIMEOUT_MS);
  const stop = () => request.abort();
  signal.addEventListener('abort', stop);
  try {
    const response = await fetch(url, { signal: request.signal });
    if (!response.ok) {
      return {
        ok: false,
        error: `The docs service answered ${response.status} for the ${label}.`,
      };
    }
    return { ok: true, value: await response.text() };
  } catch {
    if (timedOut) {
      return { ok: false, error: TIMEOUT_ERROR };
    }
    if (signal.aborted) {
      return { ok: false, error: STOPPED_ERROR };
    }
    return {
      ok: false,
      error: `The ${label} could not be loaded from the docs service.`,
    };
  } finally {
    window.clearTimeout(timer);
    signal.removeEventListener('abort', stop);
  }
};

const measureDocumentLayout = (
  html: string,
  signal: AbortSignal,
  selectors: ReadonlyArray<string> = [],
): Promise<Step<LayoutMeasurement>> =>
  new Promise(resolve => {
    if (signal.aborted) {
      resolve({ ok: false, error: STOPPED_ERROR });
      return;
    }
    const nonce = crypto.randomUUID();
    const frame = document.createElement('iframe');
    const listening = new AbortController();
    const timer = window.setTimeout(
      () => finish({ ok: false, error: TIMEOUT_ERROR }),
      FRAME_TIMEOUT_MS,
    );
    const finish = (step: Step<LayoutMeasurement>) => {
      window.clearTimeout(timer);
      listening.abort();
      frame.remove();
      resolve(step);
    };
    window.addEventListener(
      'message',
      (event: MessageEvent) => {
        const step =
          event.source === frame.contentWindow
            ? parseFrameReply(event.data, nonce)
            : null;
        if (step != null) {
          finish(step);
        }
      },
      { signal: listening.signal },
    );
    signal.addEventListener(
      'abort',
      () => finish({ ok: false, error: STOPPED_ERROR }),
      { signal: listening.signal },
    );
    frame.setAttribute('sandbox', 'allow-scripts');
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.style.cssText = FRAME_STYLE;
    frame.srcdoc = injectAfterBodyOpen(
      html,
      `<script>${layoutMeasurementScript(nonce, selectors)}</script>`,
    );
    document.body.appendChild(frame);
  });

type MeasurementCache = {
  get: (key: string) => LayoutMeasurement | null;
  set: (key: string, value: LayoutMeasurement) => void;
};

export const createMeasurementCache = (
  maxEntries: number = MEASUREMENT_CACHE_MAX_ENTRIES,
  ttlMs: number = MEASUREMENT_CACHE_TTL_MS,
  now: () => number = Date.now,
): MeasurementCache => {
  const entries = new Map<string, { value: LayoutMeasurement; at: number }>();
  return {
    get: key => {
      const entry = entries.get(key);
      if (entry == null) {
        return null;
      }
      entries.delete(key);
      if (now() - entry.at > ttlMs) {
        return null;
      }
      entries.set(key, entry);
      return entry.value;
    },
    set: (key, value) => {
      entries.delete(key);
      entries.set(key, { value, at: now() });
      for (const oldest of entries.keys()) {
        if (entries.size <= maxEntries) {
          break;
        }
        entries.delete(oldest);
      }
    },
  };
};

export const measurementKey = (
  html: string,
  selectors: ReadonlyArray<string>,
  language: string,
): string => JSON.stringify([FRAME_STYLE, language, selectors, html]);

type CheckDeps = {
  fetchHtml: (
    url: URL,
    label: string,
    signal: AbortSignal,
  ) => Promise<Step<string>>;
  measure: (
    html: string,
    signal: AbortSignal,
    selectors: ReadonlyArray<string>,
  ) => Promise<Step<LayoutMeasurement>>;
  baselineCache: MeasurementCache;
  templateCache: MeasurementCache;
};

const defaultDeps: CheckDeps = {
  fetchHtml: fetchDocumentHtml,
  measure: measureDocumentLayout,
  baselineCache: createMeasurementCache(),
  templateCache: createMeasurementCache(),
};

export const checkTemplateLayoutWith = async (
  deps: CheckDeps,
  {
    templateId,
    baselineTemplateId,
    documentId,
    language,
    documentType = 'cma_report',
  }: TemplateLayoutInput,
  selectors: ReadonlyArray<string>,
  signal: AbortSignal,
): Promise<TemplateLayoutCheckOutput> => {
  const local = new AbortController();
  const stop = () => local.abort();
  if (signal.aborted) {
    stop();
  }
  signal.addEventListener('abort', stop);
  const failure: { first: { ok: false; error: string } | null } = {
    first: null,
  };
  const measureHtml = async (
    url: URL,
    label: string,
    cache: MeasurementCache,
    measureSelectors: ReadonlyArray<string>,
  ): Promise<Step<LayoutMeasurement>> => {
    const html = await deps.fetchHtml(url, label, local.signal);
    if (!html.ok) {
      return html;
    }
    const key = measurementKey(html.value, measureSelectors, language);
    const cached = cache.get(key);
    if (cached != null) {
      return { ok: true, value: cached };
    }
    const measured = await deps.measure(
      html.value,
      local.signal,
      measureSelectors,
    );
    if (measured.ok) {
      cache.set(key, measured.value);
    }
    return measured;
  };
  const guard = async (
    step: Promise<Step<LayoutMeasurement>>,
  ): Promise<Step<LayoutMeasurement>> => {
    const result = await step;
    if (!result.ok) {
      failure.first ??= result;
      local.abort();
    }
    return result;
  };
  try {
    const [baseline, template] = await Promise.all([
      guard(
        measureHtml(
          documentHtmlUrl(
            baselineTemplateId,
            documentId,
            language,
            documentType,
          ),
          'baseline template',
          deps.baselineCache,
          [],
        ),
      ),
      guard(
        measureHtml(
          documentHtmlUrl(templateId, documentId, language, documentType),
          'template',
          deps.templateCache,
          selectors,
        ),
      ),
    ]);
    if (!baseline.ok) {
      return failure.first ?? baseline;
    }
    if (!template.ok) {
      return failure.first ?? template;
    }
    return compareLayouts(
      { documentId, language },
      baseline.value,
      template.value,
    );
  } finally {
    signal.removeEventListener('abort', stop);
  }
};

const checkTemplateLayout = (
  input: TemplateLayoutInput,
  selectors: ReadonlyArray<string>,
  signal: AbortSignal,
): Promise<TemplateLayoutCheckOutput> =>
  checkTemplateLayoutWith(defaultDeps, input, selectors, signal);

type CheckRunner = (
  input: TemplateLayoutInput,
  selectors: ReadonlyArray<string>,
  signal: AbortSignal,
) => Promise<TemplateLayoutCheckOutput>;

type EarlyRun = {
  key: string;
  controller: AbortController;
  promise: Promise<TemplateLayoutCheckOutput>;
  timer: ReturnType<typeof setTimeout>;
};

export type EarlyLayoutChecks = {
  startFromParts: (parts: ReadonlyArray<unknown>) => void;
  take: (
    target: TemplateLayoutInput,
    output: Record<string, unknown>,
    signal: AbortSignal,
  ) => Promise<TemplateLayoutCheckOutput> | null;
  abortAll: () => void;
};

export const earlyRunKey = (
  target: TemplateLayoutInput,
  output: Record<string, unknown>,
): string =>
  JSON.stringify([
    target.templateId.toLowerCase(),
    target.baselineTemplateId.toLowerCase(),
    target.documentId.toLowerCase(),
    target.language,
    output.version ?? null,
    selectorsOf(output),
  ]);

const writtenTarget = (
  output: Record<string, unknown>,
): TemplateLayoutInput | null => {
  const template = isRecord(output.template) ? output.template : {};
  const parsed = parseTemplateLayoutInput({
    templateId: template.id,
    baselineTemplateId: output.baselineTemplateId,
    documentId: output.documentId,
    language: output.language,
  });
  return output.ok === true && output.rendered === true && parsed.ok
    ? { ...parsed.input, documentType: documentTypeOf(output) }
    : null;
};

export const createEarlyLayoutChecks = (
  run: CheckRunner = checkTemplateLayout,
  ttlMs: number = EARLY_RUN_TTL_MS,
): EarlyLayoutChecks => {
  const runs = new Map<string, EarlyRun>();
  const started = new Set<string>();

  const release = (documentKey: string, early: EarlyRun) => {
    if (runs.get(documentKey) === early) {
      runs.delete(documentKey);
    }
    clearTimeout(early.timer);
  };

  const discard = (documentKey: string, early: EarlyRun) => {
    release(documentKey, early);
    early.controller.abort();
  };

  return {
    startFromParts: parts => {
      for (const part of parts) {
        if (
          !isRecord(part) ||
          !WRITE_TOOLS.some(tool => isToolPartNamed(part, tool)) ||
          part.state !== 'output-available' ||
          typeof part.toolCallId !== 'string' ||
          !isRecord(part.output) ||
          started.has(part.toolCallId)
        ) {
          continue;
        }
        started.add(part.toolCallId);
        const output = part.output;
        const target = writtenTarget(output);
        if (target == null) {
          continue;
        }
        const documentKey = target.documentId.toLowerCase();
        const previous = runs.get(documentKey);
        if (previous != null) {
          discard(documentKey, previous);
        }
        const controller = new AbortController();
        const early: EarlyRun = {
          key: earlyRunKey(target, output),
          controller,
          promise: run(target, selectorsOf(output), controller.signal).catch(
            (): TemplateLayoutCheckOutput => ({
              ok: false,
              error: 'The page check failed.',
            }),
          ),
          timer: setTimeout(() => discard(documentKey, early), ttlMs),
        };
        runs.set(documentKey, early);
      }
    },
    take: (target, output, signal) => {
      const documentKey = target.documentId.toLowerCase();
      const early = runs.get(documentKey);
      if (early == null || early.key !== earlyRunKey(target, output)) {
        return null;
      }
      release(documentKey, early);
      if (signal.aborted) {
        early.controller.abort();
      } else {
        signal.addEventListener('abort', () => early.controller.abort(), {
          once: true,
        });
      }
      return early.promise;
    },
    abortAll: () => {
      for (const [documentKey, early] of [...runs]) {
        discard(documentKey, early);
      }
      started.clear();
    },
  };
};

const onlyCheckSince = (later: ReadonlyArray<unknown>): boolean =>
  later.every(
    part =>
      !isRecord(part) ||
      isToolPartNamed(part, CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL) ||
      !(
        (typeof part.type === 'string' && part.type.startsWith('tool-')) ||
        part.type === 'dynamic-tool'
      ),
  );

export const runTemplateLayoutCheck = async (
  input: unknown,
  signal: AbortSignal,
  readMessages: () => ReadonlyArray<LayoutCheckMessage>,
  earlyChecks?: EarlyLayoutChecks,
): Promise<TemplateLayoutCheckOutput> => {
  const parsed = parseTemplateLayoutInput(input);
  if (!parsed.ok) {
    return parsed;
  }
  await new Promise(resolve => setTimeout(resolve, 0));
  const messages = readMessages();
  const written = findWritten(messages, parsed.input);
  if (written == null) {
    return { ok: false, error: NOT_PREVIEWED_ERROR };
  }
  try {
    const early = onlyCheckSince(written.later)
      ? earlyChecks?.take(parsed.input, written.output, signal)
      : null;
    const earlyResult = early == null ? null : await early;
    const result =
      earlyResult?.ok === true || (earlyResult != null && signal.aborted)
        ? earlyResult
        : await checkTemplateLayout(
            { ...parsed.input, documentType: documentTypeOf(written.output) },
            selectorsOf(written.output),
            signal,
          );
    if (!result.ok) {
      return result;
    }
    const grown = growthSinceLastCheck(
      messages,
      parsed.input,
      result.template.sections,
    );
    return grown.length === 0
      ? result
      : {
          ...result,
          grownSinceLastCheck: grown,
          note: `${
            result.note
          } Since the last check (a theme change, for example a taller header), these sections take more pages: ${grown
            .map(
              section =>
                `${section.id} (${section.previousPages} → ${section.pages})`,
            )
            .join(
              ', ',
            )}. Make them fit again with update_document_template (smaller sizes or spacing).`,
        };
  } catch (error) {
    return {
      ok: false,
      error:
        error instanceof Error
          ? `The page check failed: ${error.message}`
          : 'The page check failed.',
    };
  }
};
